import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { infoApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Info, History, Loader2, Tag } from 'lucide-react';

const dataLunga = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
};

const dataOra = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${dataLunga(iso)}, ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
};

const CopiaJobId = ({ jobId, testid }) => {
  const copia = async () => {
    try {
      await navigator.clipboard.writeText(jobId);
      toast.success('Job ID copiato');
    } catch {
      toast.error('Copia non riuscita: seleziona e copia manualmente');
    }
  };
  return (
    <div className="flex items-start gap-2">
      <code className="text-xs bg-secondary px-2 py-1 rounded break-all flex-1" data-testid={testid}>
        {jobId}
      </code>
      <Button variant="outline" size="sm" className="shrink-0" onClick={copia} data-testid={`${testid}-copia`}>
        <Copy className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
};

const InfoPage = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [caricando, setCaricando] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setInfo(await infoApi.getVersione());
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Errore nel caricamento delle info');
      } finally {
        setCaricando(false);
      }
    })();
  }, []);

  const corrente = info?.corrente;
  const storico = info?.storico || [];
  const precedenti = storico.filter((v) => v.versione !== corrente?.versione);
  const tecnico = !!info?.job_id_ambiente || !!corrente?.job_id;

  return (
    <div className="min-h-screen pb-24">
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/impostazioni')} data-testid="info-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Info &amp; Versioni</h1>
            <p className="text-xs text-muted-foreground">Versione in uso e storico dei rilasci</p>
          </div>
        </div>
      </header>

      <main className="px-4 py-5 max-w-lg mx-auto space-y-4">
        {caricando && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="info-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        )}

        {!caricando && !corrente && (
          <Card data-testid="info-vuoto">
            <CardContent className="p-4 text-sm text-muted-foreground">
              Nessuna versione registrata in questo ambiente.
            </CardContent>
          </Card>
        )}

        {corrente && (
          <Card data-testid="info-versione-corrente">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-primary" /> Versione in uso
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  <Tag className="w-3 h-3" /> v{corrente.versione}
                </span>
                <span className="text-sm font-semibold" data-testid="info-titolo">{corrente.titolo || '—'}</span>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Ultimo aggiornamento</p>
                <p className="font-semibold" data-testid="info-attivata-il">{dataLunga(corrente.attivata_il)}</p>
              </div>

              {corrente.note && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Cosa è cambiato</p>
                  <p className="text-sm" data-testid="info-note">{corrente.note}</p>
                </div>
              )}

              {tecnico && (
                <div className="space-y-2 pt-1 border-t">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Job ID della chat che ha prodotto questa versione
                    </p>
                    <CopiaJobId jobId={corrente.job_id || '—'} testid="info-job-id" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Build del {dataLunga(corrente.data_build)} · ultimo avvio {dataOra(corrente.ultimo_avvio)}
                    {corrente.avvii ? ` · ${corrente.avvii} avvii` : ''}
                  </p>
                  {info.job_id_ambiente && info.job_id_ambiente !== corrente.job_id && (
                    <p className="text-xs text-muted-foreground">
                      Job ID dell'ambiente in cui gira: <code className="break-all">{info.job_id_ambiente}</code>
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card data-testid="info-storico">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-primary" /> Versioni precedenti
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {precedenti.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="info-storico-vuoto">
                Nessuna versione precedente registrata in questo ambiente: lo storico cresce a ogni
                nuovo aggiornamento pubblicato.
              </p>
            ) : (
              precedenti.map((v) => (
                <div key={v.versione} className="p-3 rounded-lg border space-y-1.5" data-testid={`info-storico-v${v.versione}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">v{v.versione} · {v.titolo || '—'}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{dataLunga(v.attivata_il)}</span>
                  </div>
                  {v.note && <p className="text-xs text-muted-foreground">{v.note}</p>}
                  {v.job_id && <CopiaJobId jobId={v.job_id} testid={`info-job-id-v${v.versione}`} />}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default InfoPage;
